/**
 * Le contrat du **rapprochement, des écarts et de la clôture**, côté navigateur
 * (`EX-REC-003`, `EX-REC-005`, `EX-LDG-002`, `EX-BO-003`, `EX-BO-004`).
 *
 * **Ce fichier ne décide de rien.** Il transporte, et il déclare ce que le
 * serveur rend — les types suivent `ReconciliationMessages`, sans rien y
 * ajouter. `finance-maquette.test.tsx` confronte champ par champ cette
 * déclaration au **fichier Java lui-même** : recopier un contrat, c'est en
 * faire une seconde source de vérité qui vieillira (`D-35`, `D-45`).
 *
 * ## Quatre routes, et elles ne font que LIRE
 *
 * `ReconciliationApi` sert quatre lectures et pas une écriture, et son en-tête
 * dit pourquoi : importer un relevé, affecter un écart, l'instruire, le clore,
 * régulariser et clôturer une période passent toutes par
 * `POST /api/backoffice/actions/{action}`, la route unique du socle. `EX-BO-002`
 * fait porter l'habilitation sur l'**action** et non sur l'écran ; une route
 * d'écriture propre au rapprochement aurait été une seconde porte, avec son
 * propre contrôle d'accès à tenir à jour.
 *
 * Les quatre étaient **servies, éprouvées, et appelées par aucun écran** — la
 * neuvième occurrence de la famille que `D-25`, `D-30`, `D-35`, `D-45` et
 * `D-57` décrivent. C'est ce que ce fichier referme.
 *
 * ## Les montants sont des ENTIERS D'UNITÉS MINEURES, et sans devise
 *
 * `ReconciliationMessages` le dit : « les montants sont en unités mineures,
 * entiers, comme partout dans ce système ». **Et aucun de ces quatre contrats
 * ne sert de code de devise** — ni `DiscrepancyView.amount`, ni les trois
 * soldes de `CustodyView`, ni `RunView.matchedAmount`. L'écran ne peut donc pas
 * les mettre en forme comme une monnaie : écrire « F CFA » ici serait choisir
 * une devise que personne n'a servie, sur l'écran où l'on décide d'une écriture
 * comptable. Ils sont rendus comme des nombres, et l'écran dit qu'ils sont en
 * unités mineures.
 *
 * ## `gap` est SIGNÉ, et le signe porte tout le sens
 *
 * Positif, les détenteurs possèdent plus que ce que le cantonnement couvre —
 * c'est l'incident. Négatif, le cantonnement couvre davantage, ce qui est
 * l'état normal dès qu'un frais a été prélevé. Un écran qui en afficherait la
 * valeur absolue alarmerait à chaque frais ; c'est écrit dans le contrat Java,
 * et repris ici pour que personne ne « corrige » le signe en passant.
 *
 * ## `controlled` et `reconciled` ne sont pas de la décoration
 *
 * Un rapport vide n'est pas « aucun écart », c'est « **aucun contrôle ce
 * jour-là** ». Les confondre est la faute que `D-53` a laissée vivre depuis la
 * story 1.4, et les deux booléens existent pour l'empêcher. Un écran qui les
 * ignorerait rendrait « journée saine » là où il n'y a eu aucune mesure.
 */

import { ApiError } from './api-client.js';
import type { ApiClient } from './api-client.js';

/**
 * Les quatre chemins, **écrits en entier chacun**.
 *
 * Une constante de racine — `/api/backoffice/reconciliation` — aurait été plus
 * courte et fausse : ce préfixe n'est servi par aucune correspondance, et
 * `tools/ci/test_routes_appelees_et_servies.py` la lirait comme un chemin
 * appelé que personne ne sert. C'est le contrôle né de `D-35` et affûté par
 * `D-45` et `D-57` ; lui donner une racine à confondre avec une route serait
 * exactement le genre d'ambiguïté que `D-45` décrit comme « un oracle
 * silencieux ».
 */

/** La file des écarts non résolus. */
export const discrepancyQueuePath = '/api/backoffice/reconciliation/discrepancies';

/** Les périodes comptables, ouvertes et closes. */
export const accountingPeriodsPath = '/api/backoffice/reconciliation/periods';

/** Un écart et tout son historique. */
export function discrepancyPath(discrepancyId: string): string {
  return `${discrepancyQueuePath}/${encodeURIComponent(discrepancyId)}`;
}

/** Le rapport d'une journée comptable, désignée au format ISO-8601. */
export function dailyReportPath(businessDate: string): string {
  return `/api/backoffice/reconciliation/reports/${encodeURIComponent(businessDate)}`;
}

/** Le chemin de la route unique des actions du socle. */
export function backOfficeActionPath(action: string): string {
  return `/api/backoffice/actions/${action}`;
}

/** Le code de la clôture d'une période dans la matrice — `PERIOD_CLOSE`, en ✔✔. */
export const periodCloseAction = 'PERIOD_CLOSE';

/**
 * Le motif d'audit des quatre lectures.
 *
 * Une **constante** et non une saisie, pour la raison exacte qui la rend
 * constante sur la file de double validation : le contrôleur financier ouvre
 * cet écran toujours pour la même raison — contrôler le rapprochement de la
 * journée. Une invite par ouverture coûterait une frappe pour une phrase
 * identique, et les quatre lectures partent ensemble.
 *
 * Le motif de la **clôture**, lui, est écrit à la main : c'est une décision, et
 * il rejoint les deux identités dans le journal. Les confondre inscrirait
 * « contrôle du rapprochement » comme motif d'un arrêté de comptes.
 */
export const reconciliationAuditReason = 'controle-du-rapprochement';

/**
 * Un écart, exactement tel que le serveur le rend.
 *
 * `kind`, `status` et `resolution` sont des **codes** — le serveur ne sert
 * aucun libellé français, et une table de correspondance écrite ici serait la
 * liste blanche partagée de `G3` : le premier état ajouté en base la rendrait
 * fausse en silence.
 *
 * Trois champs peuvent manquer, et chacun pour une raison qui se lit :
 * `assignedTo` est absent d'un écart que personne n'a pris ; `resolvedAt` et
 * `resolution` sont absents d'un écart non résolu — et **zéro n'est pas la même
 * chose qu'absent**.
 */
export interface Discrepancy {
  readonly id: string;
  readonly kind: string;
  readonly businessDate: string;
  readonly amount: number;
  readonly detail: string;
  readonly status: string;
  readonly assignedTo: string | null;
  readonly openedAt: string;
  readonly resolvedAt: string | null;
  readonly resolution: string | null;
}

/**
 * Un moment de la vie d'un écart.
 *
 * `actorId` est un **identifiant**, pas un nom d'affichage : le serveur rend
 * l'`UUID` de `admin.bo_user`. La maquette dessine « S. Camara » ; aucune route
 * ne sert ce nom, et l'écrire ici l'inventerait. Il est absent quand le moment
 * n'a pas d'auteur humain — l'ouverture d'un écart est faite par la passe de
 * contrôle.
 */
export interface DiscrepancyEvent {
  readonly eventType: string;
  readonly actorId: string | null;
  readonly note: string;
  readonly occurredAt: string;
}

/**
 * Un écart et son histoire, rendus **ensemble**.
 *
 * Une seule réponse, et c'est délibéré côté serveur : « un écart porte son
 * investigation et sa résolution, et les trois se retrouvent ensemble ». Deux
 * routes auraient laissé l'écran libre de n'en appeler qu'une.
 */
export interface DiscrepancyDetail {
  readonly discrepancy: Discrepancy;
  readonly history: readonly DiscrepancyEvent[];
}

/**
 * Le constat de cantonnement d'une journée.
 *
 * `gap` est **signé** : voir l'en-tête. `blocked` dit que le constat a bloqué
 * quelque chose, et `severity` est un **code**, pas une phrase.
 */
export interface Custody {
  readonly accountsPhotographed: number;
  readonly holdersBalance: number;
  readonly trustBalance: number;
  readonly gap: number;
  readonly severity: string;
  readonly blocked: boolean;
  readonly computedAt: string;
}

/**
 * Le rapprochement d'une journée — **un** relevé, et non cinq sources.
 *
 * La maquette dessine cinq lignes de sources rapprochées ; le contrat sert un
 * seul `statementId` par journée, et **aucun montant attendu**. La colonne
 * « Attendu » du dessin n'a donc aucune valeur servie derrière elle, et le taux
 * de rapprochement qu'elle permettrait de calculer n'existe nulle part. Voir
 * `FinanceScreen`, qui déclare l'écart plutôt que de le combler.
 */
export interface ReconciliationRun {
  readonly statementId: string;
  readonly matchedCount: number;
  readonly matchedAmount: number;
  readonly internalOrphanCount: number;
  readonly externalOrphanCount: number;
  readonly ranAt: string;
}

/**
 * Le rapport quotidien.
 *
 * `custody` et `reconciliation` peuvent manquer **séparément** : une journée
 * peut avoir été cantonnée sans avoir été rapprochée, et l'inverse. C'est
 * pourquoi `controlled` et `reconciled` sont rendus en plus — ils disent quelle
 * mesure a eu lieu, là où une absence de ligne ne dit rien.
 */
export interface DailyReport {
  readonly businessDate: string;
  readonly controlled: boolean;
  readonly reconciled: boolean;
  readonly custody: Custody | null;
  readonly reconciliation: ReconciliationRun | null;
  readonly discrepancies: readonly Discrepancy[];
}

/**
 * Une période comptable, ouverte ou verrouillée.
 *
 * `closedAt`, `closedBy` et `approvedBy` sont absents tant qu'elle n'est pas
 * close — et **c'est `closedAt` qui dit qu'elle ne l'est pas**, non son
 * `status`, qui est un code que le navigateur n'a pas à connaître.
 */
export interface AccountingPeriod {
  readonly id: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly status: string;
  readonly closedAt: string | null;
  readonly closedBy: string | null;
  readonly approvedBy: string | null;
}

/**
 * Ce que le socle répond à une action demandée.
 *
 * `approvalRequestId` est **la preuve que la demande attend une seconde
 * signature** : le serveur ne le sert que lorsqu'il a mis l'action en attente.
 * L'écran s'en sert plutôt que de comparer `outcome` à un code écrit ici —
 * comparer à une constante du navigateur ferait dépendre l'affichage d'une
 * liste blanche que le serveur peut allonger sans prévenir.
 */
export interface ActionAcknowledgement {
  readonly outcome: string;
  readonly approvalRequestId: string | null;
  readonly detail: string | null;
}

/**
 * Lit la file des écarts non résolus.
 *
 * **Elle est complète** : `DiscrepancyRepository.findOpen` ne porte ni borne ni
 * curseur, et le serveur journalise lui-même `openDiscrepancies = taille de la
 * file`. Le nombre de lignes reçues est donc bien le nombre d'écarts ouverts —
 * ce n'est pas un total inventé par l'écran, c'est ce que le serveur a rendu.
 *
 * Aucune capture d'erreur : l'`ApiError` du client porte déjà sa nature et sa
 * clé de message. L'avaler pour rendre une liste vide ferait confondre « aucun
 * écart n'est ouvert » — un fait, et le meilleur des faits sur cet écran — avec
 * « le serveur a refusé », qui n'en est pas un. Sur un écran de rapprochement,
 * la confusion se lirait comme « tout va bien », c'est-à-dire l'inverse de ce
 * qui s'est passé.
 */
export async function fetchDiscrepancyQueue(
  client: ApiClient,
  reason: string = reconciliationAuditReason,
): Promise<readonly Discrepancy[]> {
  const recue = await client.request<unknown>(
    `${discrepancyQueuePath}?reason=${encodeURIComponent(reason)}`,
  );
  if (!Array.isArray(recue) || !recue.every(estUnEcart)) throw malForme(client);
  return recue as readonly Discrepancy[];
}

/** Lit un écart et tout son historique. */
export async function fetchDiscrepancy(
  client: ApiClient,
  discrepancyId: string,
  reason: string = reconciliationAuditReason,
): Promise<DiscrepancyDetail> {
  const recue = await client.request<unknown>(
    `${discrepancyPath(discrepancyId)}?reason=${encodeURIComponent(reason)}`,
  );
  if (
    !estUnObjet(recue) ||
    !estUnEcart(recue.discrepancy) ||
    !Array.isArray(recue.history) ||
    !recue.history.every(estUnMoment)
  ) {
    throw malForme(client);
  }
  return recue as unknown as DiscrepancyDetail;
}

/**
 * Lit le rapport d'une journée.
 *
 * La journée est passée telle que l'opérateur l'a écrite : le serveur refuse
 * lui-même ce qui n'est pas une date ISO-8601, avec un message qui le dit. La
 * valider ici en plus ferait deux règles de format à tenir à jour, et c'est
 * toujours la seconde qu'on oublie.
 */
export async function fetchDailyReport(
  client: ApiClient,
  businessDate: string,
  reason: string = reconciliationAuditReason,
): Promise<DailyReport> {
  const recue = await client.request<unknown>(
    `${dailyReportPath(businessDate)}?reason=${encodeURIComponent(reason)}`,
  );
  if (!estUnRapport(recue)) throw malForme(client);
  return recue;
}

/** Lit les périodes comptables, ouvertes et closes. */
export async function fetchAccountingPeriods(
  client: ApiClient,
  reason: string = reconciliationAuditReason,
): Promise<readonly AccountingPeriod[]> {
  const recue = await client.request<unknown>(
    `${accountingPeriodsPath}?reason=${encodeURIComponent(reason)}`,
  );
  if (!Array.isArray(recue) || !recue.every(estUnePeriode)) throw malForme(client);
  return recue as readonly AccountingPeriod[];
}

/**
 * **Demande** la clôture d'une période. Elle ne la prononce pas.
 *
 * `PERIOD_CLOSE` est en ✔✔ dans la matrice pour la finance : le socle met la
 * demande en attente et rend `202` avec l'identifiant de la demande. La
 * seconde signature se donne dans la **file de double validation** déjà livrée
 * — celle du tableau de bord —, et nulle part ailleurs. C'est le point entier
 * d'`EX-BO-003` : une clôture qu'un seul agent pourrait prononcer serait une
 * clôture sans second regard, et la balance qu'elle publie est opposable.
 *
 * Le motif est **obligatoire** côté serveur (`requireReason`) ; il l'est donc
 * aussi côté écran, sans quoi l'agent lirait « le serveur a rencontré une
 * erreur » là où il manque une phrase de sa main.
 */
export async function requestPeriodClosure(
  client: ApiClient,
  periodId: string,
  reason: string,
): Promise<ActionAcknowledgement> {
  const recue = await client.request<unknown>(backOfficeActionPath(periodCloseAction), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // `entityRef` désigne ce sur quoi on agit — c'est ce que le journal d'audit
    // inscrit ; `payload.periodId` est ce que `PeriodCloseExecutor` lit. Les
    // deux portent la même période, et ce n'est pas une redondance : les lire
    // au même endroit ferait une entrée d'audit qui ne désigne pas l'objet, ou
    // une clôture qui verrouille on ne sait quoi.
    body: JSON.stringify({ entityRef: periodId, reason, payload: { periodId } }),
  });
  if (!estUnAccuse(recue)) throw malForme(client);
  return recue;
}

/**
 * Le refus typé d'une réponse qui n'a pas la forme attendue.
 *
 * **Ce qui revient du réseau est vérifié avant d'être cru.** Une réponse à
 * moitié formée ferait lever l'écran au rendu — une page blanche au lieu d'un
 * rapprochement —, ou, pire sur une liste, se lirait comme « rien à
 * signaler ». Rendre une erreur typée fait dire « le serveur a répondu autre
 * chose », ce qui est exactement le cas.
 */
function malForme(client: ApiClient): ApiError {
  return new ApiError({
    kind: 'server',
    status: null,
    correlationId: client.lastCorrelationId ?? '',
  });
}

function estUnObjet(valeur: unknown): valeur is Record<string, unknown> {
  return typeof valeur === 'object' && valeur !== null;
}

function texteOuAbsent(valeur: unknown): boolean {
  return valeur === null || typeof valeur === 'string';
}

function estUnEcart(valeur: unknown): boolean {
  if (!estUnObjet(valeur)) return false;
  return (
    typeof valeur.id === 'string' &&
    typeof valeur.kind === 'string' &&
    typeof valeur.businessDate === 'string' &&
    typeof valeur.amount === 'number' &&
    typeof valeur.detail === 'string' &&
    typeof valeur.status === 'string' &&
    texteOuAbsent(valeur.assignedTo) &&
    typeof valeur.openedAt === 'string' &&
    texteOuAbsent(valeur.resolvedAt) &&
    texteOuAbsent(valeur.resolution)
  );
}

function estUnMoment(valeur: unknown): boolean {
  if (!estUnObjet(valeur)) return false;
  return (
    typeof valeur.eventType === 'string' &&
    texteOuAbsent(valeur.actorId) &&
    typeof valeur.note === 'string' &&
    typeof valeur.occurredAt === 'string'
  );
}

function estUnCantonnement(valeur: unknown): boolean {
  if (!estUnObjet(valeur)) return false;
  return (
    typeof valeur.accountsPhotographed === 'number' &&
    typeof valeur.holdersBalance === 'number' &&
    typeof valeur.trustBalance === 'number' &&
    typeof valeur.gap === 'number' &&
    typeof valeur.severity === 'string' &&
    typeof valeur.blocked === 'boolean' &&
    typeof valeur.computedAt === 'string'
  );
}

function estUnRapprochement(valeur: unknown): boolean {
  if (!estUnObjet(valeur)) return false;
  return (
    typeof valeur.statementId === 'string' &&
    typeof valeur.matchedCount === 'number' &&
    typeof valeur.matchedAmount === 'number' &&
    typeof valeur.internalOrphanCount === 'number' &&
    typeof valeur.externalOrphanCount === 'number' &&
    typeof valeur.ranAt === 'string'
  );
}

/**
 * La forme d'un rapport quotidien.
 *
 * **`controlled` et `reconciled` sont exigés comme des booléens**, et non
 * lus mollement : un champ absent vaudrait `undefined`, donc faux, donc
 * « aucun contrôle ce jour-là » sur une journée parfaitement contrôlée. Le
 * refus est le bon comportement — il dit que la réponse n'est pas celle du
 * contrat, au lieu d'en inventer la lecture.
 */
function estUnRapport(valeur: unknown): valeur is DailyReport {
  if (!estUnObjet(valeur)) return false;
  return (
    typeof valeur.businessDate === 'string' &&
    typeof valeur.controlled === 'boolean' &&
    typeof valeur.reconciled === 'boolean' &&
    (valeur.custody === null || estUnCantonnement(valeur.custody)) &&
    (valeur.reconciliation === null || estUnRapprochement(valeur.reconciliation)) &&
    Array.isArray(valeur.discrepancies) &&
    valeur.discrepancies.every(estUnEcart)
  );
}

function estUnePeriode(valeur: unknown): boolean {
  if (!estUnObjet(valeur)) return false;
  return (
    typeof valeur.id === 'string' &&
    typeof valeur.periodStart === 'string' &&
    typeof valeur.periodEnd === 'string' &&
    typeof valeur.status === 'string' &&
    texteOuAbsent(valeur.closedAt) &&
    texteOuAbsent(valeur.closedBy) &&
    texteOuAbsent(valeur.approvedBy)
  );
}

function estUnAccuse(valeur: unknown): valeur is ActionAcknowledgement {
  if (!estUnObjet(valeur)) return false;
  return (
    typeof valeur.outcome === 'string' &&
    texteOuAbsent(valeur.approvalRequestId) &&
    texteOuAbsent(valeur.detail)
  );
}
